import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import type { Lead, Cotizacion, Conversacion, EtapaPipeline } from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/ui'
import { IconPlus } from '@/components/icons/Icons'

/* ─── Constants ─── */

const ETAPA_CONFIG: Record<EtapaPipeline, { label: string; color: string; bg: string }> = {
  // Prospección outbound: no entra en el embudo (todavía no se contactó a nadie),
  // pero la etapa existe y el mapa tiene que cubrirla.
  prospecto: { label: 'Prospecto', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  lead: { label: 'Lead', color: 'var(--primary-light)', bg: 'color-mix(in srgb, var(--primary-light) 12%, transparent)' },
  contactado: { label: 'Contactado', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  diagnostico: { label: 'Diagnóstico', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  cotizado: { label: 'Cotizado', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  negociacion: { label: 'Negociación', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  cerrado: { label: 'Cerrado', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
}

const ETAPAS_ORDER: EtapaPipeline[] = ['lead', 'contactado', 'diagnostico', 'cotizado', 'negociacion', 'cerrado']

const ESTADO_COT_CONFIG: Record<string, { label: string; color: string }> = {
  borrador: { label: 'Borrador', color: 'var(--text-muted)' },
  enviada: { label: 'Enviada', color: 'var(--info)' },
  aceptada: { label: 'Aceptada', color: 'var(--success)' },
  rechazada: { label: 'Rechazada', color: 'var(--danger)' },
  en_negociacion: { label: 'Negociación', color: 'var(--warning)' },
}

/* ═════════════════════════════════════════════════════════════════════
   COMPONENT
   ═════════════════════════════════════════════════════════════════════ */

export default function DashboardCRM() {
  const api = useApi()

  const { data: leads = [], isLoading: l1 } = useQuery<Lead[]>({
    queryKey: ['crm-leads'],
    queryFn: () => api.get('/api/leads'),
    staleTime: 30_000,
  })

  const { data: cotizaciones = [], isLoading: l2 } = useQuery<Cotizacion[]>({
    queryKey: ['crm-cotizaciones'],
    queryFn: () => api.get('/api/cotizaciones'),
    staleTime: 30_000,
  })

  const { data: conversaciones = [], isLoading: l3 } = useQuery<Conversacion[]>({
    queryKey: ['crm-conversaciones'],
    queryFn: () => api.get('/api/chat/conversaciones'),
    staleTime: 30_000,
  })

  const loading = l1 || l2 || l3

  /* ─── KPIs ─── */
  const kpis = useMemo(() => {
    const leadsActivos = leads.filter(l => l.etapa !== 'cerrado').length
    const leadsCerrados = leads.filter(l => l.etapa === 'cerrado').length
    const conversionRate = leads.length > 0 ? Math.round((leadsCerrados / leads.length) * 100) : 0

    const cotPendientes = cotizaciones.filter(c => c.estado === 'enviada' || c.estado === 'en_negociacion')
    const cotAceptadas = cotizaciones.filter(c => c.estado === 'aceptada')
    const totalCotPendienteUSD = cotPendientes.filter(c => c.moneda === 'USD').reduce((s, c) => s + c.total, 0)
    const totalCotPendientePEN = cotPendientes.filter(c => c.moneda === 'PEN').reduce((s, c) => s + c.total, 0)
    const totalCotCerradoUSD = cotAceptadas.filter(c => c.moneda === 'USD').reduce((s, c) => s + c.total, 0)
    const totalCotCerradoPEN = cotAceptadas.filter(c => c.moneda === 'PEN').reduce((s, c) => s + c.total, 0)

    const convAbiertas = conversaciones.filter(c => c.estado === 'abierta').length
    const noLeidos = conversaciones.reduce((s, c) => s + (c.mensajes_no_leidos || 0), 0)

    return {
      leadsActivos, leadsCerrados, totalLeads: leads.length, conversionRate,
      cotPendientes: cotPendientes.length, cotAceptadas: cotAceptadas.length,
      totalCotPendienteUSD, totalCotPendientePEN,
      totalCotCerradoUSD, totalCotCerradoPEN,
      convAbiertas, noLeidos,
    }
  }, [leads, cotizaciones, conversaciones])

  /* ─── Funnel data ─── */
  const funnelData = useMemo(() => {
    const counts: Record<EtapaPipeline, number> = {
      prospecto: 0, lead: 0, contactado: 0, diagnostico: 0, cotizado: 0, negociacion: 0, cerrado: 0,
    }
    for (const l of leads) counts[l.etapa]++
    return ETAPAS_ORDER.map(etapa => ({
      etapa,
      count: counts[etapa],
      ...ETAPA_CONFIG[etapa],
    }))
  }, [leads])

  /* ─── Recent cotizaciones ─── */
  const recentCot = useMemo(() => {
    return [...cotizaciones]
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
      .slice(0, 6)
  }, [cotizaciones])

  /* ─── Recent conversations ─── */
  const recentConv = useMemo(() => {
    return [...conversaciones]
      .filter(c => c.ultimo_mensaje)
      .sort((a, b) => (b.ultimo_mensaje_at || '').localeCompare(a.ultimo_mensaje_at || ''))
      .slice(0, 6)
  }, [conversaciones])

  /* ─── Loading ─── */
  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'CRM' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  /* ═══════════════════════ RENDER ═══════════════════════ */

  return (
    <div className="page-enter" id="dashboard-crm">
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes funnelGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes countUp { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      <PageHeader
        breadcrumbs={[{ label: 'CRM' }]}
        actions={
          <Link to="/pipeline" className="btn btn-primary">
            <IconPlus size={16} /> Nuevo Lead
          </Link>
        }
      />

      {/* ═══════════════════════════════════════════════════════════════
           KPI CARDS
           ═══════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
        marginBottom: 14,
      }}>
        {/* Leads Activos */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(139,92,246,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Leads activos</span>
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--primary-light)', letterSpacing: '-0.02em', animation: 'countUp 0.5s ease 0.2s both' }}>
            {kpis.leadsActivos}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {kpis.totalLeads} totales &middot; {kpis.conversionRate}% conversión
          </div>
        </div>

        {/* Cotizaciones Pendientes */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease 0.05s both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, color-mix(in srgb, var(--warning) 10%, transparent), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Cotizaciones pendientes</span>
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--warning)', letterSpacing: '-0.02em', animation: 'countUp 0.5s ease 0.25s both' }}>
            {kpis.cotPendientes}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {kpis.totalCotPendienteUSD > 0 && formatCurrency(kpis.totalCotPendienteUSD, 'USD')}
            {kpis.totalCotPendienteUSD > 0 && kpis.totalCotPendientePEN > 0 && ' + '}
            {kpis.totalCotPendientePEN > 0 && formatCurrency(kpis.totalCotPendientePEN, 'PEN')}
            {kpis.totalCotPendienteUSD === 0 && kpis.totalCotPendientePEN === 0 && '—'}
          </div>
        </div>

        {/* Cotizaciones Cerradas */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 16px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.4s ease 0.1s both',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 60, height: 60,
            background: 'radial-gradient(circle at top right, rgba(16,185,129,0.10), transparent 70%)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'color-mix(in srgb, var(--success) 12%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Ventas cerradas</span>
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success)', letterSpacing: '-0.02em', animation: 'countUp 0.5s ease 0.3s both' }}>
            {kpis.cotAceptadas}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {kpis.totalCotCerradoUSD > 0 && formatCurrency(kpis.totalCotCerradoUSD, 'USD')}
            {kpis.totalCotCerradoUSD > 0 && kpis.totalCotCerradoPEN > 0 && ' + '}
            {kpis.totalCotCerradoPEN > 0 && formatCurrency(kpis.totalCotCerradoPEN, 'PEN')}
            {kpis.totalCotCerradoUSD === 0 && kpis.totalCotCerradoPEN === 0 && '—'}
          </div>
        </div>

        {/* Conversaciones */}
        <Link to="/conversaciones" style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '14px 16px',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.4s ease 0.15s both',
            cursor: 'pointer',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            height: '100%',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--info)'
            e.currentTarget.style.boxShadow = '0 0 0 1px var(--info), 0 4px 12px color-mix(in srgb, var(--info) 15%, transparent)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--border)'
            e.currentTarget.style.boxShadow = 'none'
          }}
          >
            <div style={{
              position: 'absolute', top: 0, right: 0, width: 60, height: 60,
              background: 'radial-gradient(circle at top right, color-mix(in srgb, var(--info) 10%, transparent), transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: 'color-mix(in srgb, var(--info) 12%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Conversaciones</span>
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--info)', letterSpacing: '-0.02em', animation: 'countUp 0.5s ease 0.35s both' }}>
              {kpis.convAbiertas}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
              abiertas{kpis.noLeidos > 0 && <span style={{ color: 'var(--danger)', fontWeight: 600 }}> &middot; {kpis.noLeidos} sin leer</span>}
            </div>
          </div>
        </Link>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
           FUNNEL — Pipeline de Ventas
           ═══════════════════════════════════════════════════════════════ */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 14,
        animation: 'fadeInUp 0.5s ease 0.2s both',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Pipeline de Ventas
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Distribución de oportunidades por etapa
            </p>
          </div>
          <Link
            to="/pipeline"
            style={{
              fontSize: '0.75rem', color: 'var(--primary)', textDecoration: 'none',
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            Ver Pipeline
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </Link>
        </div>

        {/* Funnel bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {funnelData.map((item, idx) => {
            const maxCount = Math.max(...funnelData.map(f => f.count), 1)
            const pct = (item.count / maxCount) * 100
            return (
              <div key={item.etapa} style={{ display: 'flex', alignItems: 'center', gap: 12, animation: `fadeInUp 0.4s ease ${0.25 + idx * 0.06}s both` }}>
                <div style={{ width: 90, flexShrink: 0 }}>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 600, color: item.color,
                    background: item.bg, padding: '3px 8px', borderRadius: 6,
                    display: 'inline-block',
                  }}>
                    {item.label}
                  </span>
                </div>
                <div style={{ flex: 1, height: 28, background: 'var(--bg-primary)', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.max(pct, item.count > 0 ? 4 : 0)}%`,
                    background: `linear-gradient(90deg, ${item.color}22, ${item.color}44)`,
                    borderRadius: 8,
                    borderLeft: item.count > 0 ? `3px solid ${item.color}` : 'none',
                    transition: 'width 0.6s ease',
                    transformOrigin: 'left',
                    animation: `funnelGrow 0.8s ease ${0.3 + idx * 0.08}s both`,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 10,
                  }}>
                    {item.count > 0 && (
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: item.color }}>
                        {item.count}
                      </span>
                    )}
                  </div>
                  {item.count === 0 && (
                    <span style={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      fontSize: '0.72rem', color: 'var(--text-muted)',
                    }}>—</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
           BOTTOM ROW — Cotizaciones Recientes + Conversaciones Recientes
           ═══════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}>

        {/* ── LEFT: Cotizaciones Recientes ── */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '16px 18px',
          animation: 'fadeInUp 0.5s ease 0.35s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Cotizaciones Recientes
            </h2>
            <Link to="/cotizaciones" style={{ fontSize: '0.7rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>
              Ver todas →
            </Link>
          </div>

          {recentCot.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Sin cotizaciones aún
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentCot.map((cot, idx) => {
                const estadoConf = ESTADO_COT_CONFIG[cot.estado] || { label: cot.estado, color: 'var(--text-muted)' }
                return (
                  <Link
                    key={cot.id}
                    to={`/cotizaciones/${cot.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none',
                      padding: '8px 10px', borderRadius: 8,
                      transition: 'background 0.15s',
                      animation: `fadeInUp 0.3s ease ${0.4 + idx * 0.04}s both`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-primary)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cot.numero} — {cot.cliente_nombre || 'Sin cliente'}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {cot.proyecto_nombre || 'Sin proyecto'} &middot; {formatDate(cot.created_at)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {formatCurrency(cot.total, cot.moneda as 'USD' | 'PEN')}
                      </div>
                      <span style={{
                        fontSize: '0.62rem', fontWeight: 600,
                        color: estadoConf.color,
                        background: `${estadoConf.color}18`,
                        padding: '2px 6px', borderRadius: 4,
                      }}>
                        {estadoConf.label}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT: Conversaciones Recientes ── */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '16px 18px',
          animation: 'fadeInUp 0.5s ease 0.4s both',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Conversaciones Recientes
            </h2>
            <Link to="/conversaciones" style={{ fontSize: '0.7rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>
              Ver todas →
            </Link>
          </div>

          {recentConv.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              Sin conversaciones activas
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentConv.map((conv, idx) => {
                const nombre = (conv.metadata as Record<string, string>)?.nombre || (conv.metadata as Record<string, string>)?.contacto_nombre || conv.chat_id
                const canalIcon: Record<string, string> = {
                  whatsapp: '💬', email: '📧', web_chat: '🌐', sms: '📱', portal: '🔗',
                }
                return (
                  <Link
                    key={conv.id}
                    to={`/conversaciones/${conv.canal}/all/${conv.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none',
                      padding: '8px 10px', borderRadius: 8,
                      transition: 'background 0.15s',
                      animation: `fadeInUp 0.3s ease ${0.45 + idx * 0.04}s both`,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-primary)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9rem', flexShrink: 0,
                    }}>
                      {canalIcon[conv.canal] || '💬'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nombre}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.ultimo_mensaje}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {conv.mensajes_no_leidos > 0 && (
                        <span style={{
                          background: 'var(--danger)', color: 'var(--on-danger)', fontSize: '0.65rem', fontWeight: 700,
                          padding: '2px 7px', borderRadius: 10, minWidth: 20, display: 'inline-block', textAlign: 'center',
                        }}>
                          {conv.mensajes_no_leidos}
                        </span>
                      )}
                      {conv.ultimo_mensaje_at && (
                        <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 3 }}>
                          {formatDate(conv.ultimo_mensaje_at)}
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
